import { useEffect, useState } from "react";
import api from "../../lib/api";
import { X, Warning, PaperPlaneTilt, ChatTeardropDots } from "@phosphor-icons/react";

/**
 * SMS broadcast modal for an instance. Enforces the preview→confirm flow:
 *  1. Operator types a message + can remove individual recipients
 *  2. Operator clicks "Preview" to see exactly what each person will receive
 *  3. "Send to N" button is disabled until preview has been viewed
 *  4. Server independently re-derives the party list and intersects (stale-screen protection)
 */
export default function SmsBroadcastModal({ instance, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]); // [{name, phone, role}]
  const [excluded, setExcluded] = useState(new Set()); // phone strings
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [tourTitle, setTourTitle] = useState("");
  const [autoPrefix, setAutoPrefix] = useState(true);
  const [message, setMessage] = useState("");
  const [previewed, setPreviewed] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/instances/${instance.instance_id}/sms-recipients`);
        if (cancelled) return;
        setRecipients(r.data.recipients || []);
        setSmsConfigured(!!r.data.sms_configured);
        setTourTitle(r.data.tour_title || instance.tour_title || "");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instance.instance_id, instance.tour_title]);

  const prefix = autoPrefix ? `Mystery Served · ${tourTitle || instance.tour_title} (${instance.starts_at_local}): ` : "";
  const fullMessage = `${prefix}${message}`.trim();
  const charCount = fullMessage.length;
  const overLimit = charCount > 480;
  const segments = Math.ceil(charCount / 160) || 0;

  const active = recipients.filter(r => !excluded.has(r.phone));
  const canSend = previewed && !overLimit && message.trim().length > 0 && active.length > 0 && !sending && !result;

  const toggleExclude = (phone) => {
    const next = new Set(excluded);
    if (next.has(phone)) next.delete(phone); else next.add(phone);
    setExcluded(next);
    setPreviewed(false); // require re-preview after editing recipients
  };

  const handleMessageChange = (v) => {
    setMessage(v);
    setPreviewed(false);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/instances/${instance.instance_id}/sms-broadcast`, {
        message: fullMessage,
        recipient_phones: active.map(r => r.phone),
      });
      setResult(r.data);
      onSent?.();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Send failed" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="sms-broadcast-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <header className="flex items-start justify-between p-6 border-b border-ms-edge">
          <div>
            <p className="label-eyebrow text-ms-stone text-xs flex items-center gap-2">
              <ChatTeardropDots size={14} /> Message the party
            </p>
            <h2 className="serif text-2xl text-ms-earth mt-1">{instance.starts_at_local} · {tourTitle || instance.tour_title}</h2>
          </div>
          <button onClick={onClose} className="p-1 text-ms-stone hover:text-ms-earth" data-testid="sms-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </header>

        {!smsConfigured && (
          <div className="mx-6 mt-4 p-3 bg-ms-warn/10 border border-ms-warn/30 rounded-md text-xs text-ms-earth flex items-start gap-2" data-testid="sms-mock-warning">
            <Warning size={14} className="text-ms-warn mt-0.5 shrink-0" />
            <span>Twilio not configured — messages will be <b>mock-logged</b> only (no real SMS sent). Add Twilio creds in <code>backend/.env</code> to enable live sends.</span>
          </div>
        )}

        <div className="p-6 space-y-5">
          {/* Compose */}
          <div>
            <label className="label-eyebrow text-ms-stone text-xs">Message</label>
            <label className="ml-4 inline-flex items-center gap-1.5 text-xs text-ms-stone cursor-pointer">
              <input type="checkbox" checked={autoPrefix} onChange={e => { setAutoPrefix(e.target.checked); setPreviewed(false); }} data-testid="sms-auto-prefix" />
              Auto-prepend tour + time
            </label>
            <textarea
              value={message}
              onChange={e => handleMessageChange(e.target.value)}
              placeholder="Meeting point moved — see you at the original block instead. Reply if confused."
              rows={4}
              className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-ms-brass"
              data-testid="sms-message-input"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={overLimit ? "text-ms-danger" : "text-ms-stone"} data-testid="sms-char-count">
                {charCount}/480 · {segments} SMS segment{segments === 1 ? "" : "s"}
              </span>
              {message.trim() && (
                <button onClick={() => setPreviewed(true)} className="text-xs text-ms-brass hover:text-ms-brass-lo label-eyebrow" data-testid="sms-preview-btn">
                  Preview →
                </button>
              )}
            </div>
          </div>

          {/* Recipient chips */}
          <div>
            <div className="flex items-center justify-between">
              <p className="label-eyebrow text-ms-stone text-xs">Recipients ({active.length}{excluded.size > 0 ? ` · ${excluded.size} excluded` : ""})</p>
              {recipients.length > 0 && excluded.size > 0 && (
                <button onClick={() => { setExcluded(new Set()); setPreviewed(false); }} className="text-xs text-ms-brass label-eyebrow" data-testid="sms-restore-all">
                  Restore all
                </button>
              )}
            </div>
            {loading ? (
              <p className="mt-3 text-sm text-ms-stone">Loading recipients…</p>
            ) : recipients.length === 0 ? (
              <p className="mt-3 text-sm text-ms-stone" data-testid="sms-no-recipients">
                No SMS-able phones on this instance. Lead and co-guests need a valid 10+ digit phone.
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="sms-recipient-chips">
                {recipients.map(r => {
                  const ex = excluded.has(r.phone);
                  return (
                    <button
                      key={r.phone}
                      onClick={() => toggleExclude(r.phone)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-all ${
                        ex
                          ? "border-ms-edge text-ms-stone line-through opacity-60"
                          : r.role === "lead"
                            ? "border-ms-earth bg-ms-earth/5 text-ms-earth"
                            : "border-ms-edge text-ms-earth bg-white"
                      }`}
                      data-testid={`sms-recipient-${r.phone}`}
                      title={ex ? "Click to include" : "Click to exclude"}
                    >
                      <span>{r.name}</span>
                      <span className="text-ms-stone">{r.phone}</span>
                      {r.role === "lead" && <span className="text-[9px] uppercase tracking-wider text-ms-brass">lead</span>}
                      {!ex && <X size={10} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Preview block */}
          {previewed && message.trim() && (
            <div className="border border-ms-edge rounded-xl p-4 bg-ms-cream/40" data-testid="sms-preview-block">
              <p className="label-eyebrow text-ms-stone text-xs mb-2">Exact message preview</p>
              <p className="text-sm text-ms-earth whitespace-pre-wrap font-mono leading-relaxed">{fullMessage}</p>
              <p className="mt-3 text-xs text-ms-stone">
                Will be sent to <b className="text-ms-earth">{active.length}</b> recipient{active.length === 1 ? "" : "s"}: {active.map(r => r.name).join(", ")}
              </p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`border rounded-xl p-4 text-sm ${result.error ? "border-ms-danger bg-ms-danger/5 text-ms-danger" : "border-ms-success bg-ms-success/5 text-ms-success"}`} data-testid="sms-result">
              {result.error ? (
                <p><b>Failed:</b> {result.error}</p>
              ) : (
                <div>
                  <p className="text-ms-earth"><b className="text-ms-success">Sent {result.sent_count}</b>{result.failed_count > 0 ? ` · ${result.failed_count} failed` : ""}{result.transport === "mock" ? " (mock-logged)" : ""}.</p>
                  {result.failed_count > 0 && (
                    <ul className="mt-2 text-xs text-ms-warn list-disc list-inside">
                      {result.failed.map((f, i) => <li key={i}>{f.name} ({f.phone})</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 p-6 border-t border-ms-edge bg-ms-cream/30">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-ms-edge rounded-md hover:bg-white" data-testid="sms-cancel">
            {result && !result.error ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="inline-flex items-center gap-2 px-5 py-2 bg-ms-earth text-ms-cream rounded-md text-sm label-eyebrow disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ms-brass transition-colors"
              data-testid="sms-send-btn"
              title={!previewed ? "Preview the message first" : ""}
            >
              <PaperPlaneTilt size={14} />
              {sending ? "Sending…" : `Send to ${active.length}`}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
